import { useEffect, useState } from "react";
import { Bell, Check, MessageSquare, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  deleteNotification,
  fetchNotificationCount,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
} from "@/lib/api";
import { pickText } from "@/lib/content";

export const NotificationBell = ({ language, vipEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCount = async () => {
      if (!vipEmail) return;
      try {
        const data = await fetchNotificationCount(vipEmail);
        setUnreadCount(data.unread);
      } catch (error) {
        console.error("Failed to load notification count:", error);
      }
    };
    loadCount();
    // Poll every 30 seconds
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, [vipEmail]);

  const loadNotifications = async () => {
    if (!vipEmail) return;
    try {
      setLoading(true);
      const data = await fetchNotifications(vipEmail, 20);
      setNotifications(data);
    } catch (error) {
      console.error("Failed to load notifications:", error);
      toast.error(pickText(language, "Unable to load notifications.", "Impossibile caricare le notifiche."));
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    loadNotifications();
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationsRead(vipEmail, [notificationId]);
      setNotifications((current) =>
        current.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(vipEmail);
      setNotifications((current) => current.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success(pickText(language, "All notifications marked as read.", "Tutte le notifiche segnate come lette."));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId, vipEmail);
      setNotifications((current) => current.filter((n) => n.id !== notificationId));
      toast.success(pickText(language, "Notification deleted.", "Notifica eliminata."));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  if (!vipEmail) return null;

  return (
    <div className="relative">
      <button
        className="ghost-button relative"
        data-testid="notification-bell"
        onClick={handleOpen}
        type="button"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-black"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div
            className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[90vw] overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0a0a0e] shadow-2xl"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-4">
              <p className="text-xs uppercase tracking-[0.24em] text-primary">
                {pickText(language, "Notifications", "Notifiche")}
              </p>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    className="text-xs text-slate-400 hover:text-primary"
                    onClick={handleMarkAllRead}
                    type="button"
                  >
                    {pickText(language, "Mark all read", "Segna tutto letto")}
                  </button>
                )}
                <button
                  className="text-slate-400 hover:text-white"
                  onClick={() => setIsOpen(false)}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-sm text-slate-400">
                  {pickText(language, "Loading...", "Caricamento...")}
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  {pickText(language, "No notifications yet.", "Nessuna notifica ancora.")}
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    className={`relative border-b border-white/5 p-4 transition-colors hover:bg-white/5 ${
                      !notification.read ? "bg-primary/5" : ""
                    }`}
                    data-testid={`notification-item-${notification.id}`}
                    key={notification.id}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <MessageSquare className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <p className="text-xs text-slate-400">
                          {notification.from_user} {pickText(language, "replied to", "ha risposto a")}
                        </p>
                        <Link
                          className="mt-1 block truncate text-sm text-foreground hover:text-primary"
                          onClick={() => {
                            handleMarkRead(notification.id);
                            setIsOpen(false);
                          }}
                          to={`/community?thread=${notification.thread_id}`}
                        >
                          {notification.thread_title}
                        </Link>
                        <p className="mt-1 truncate text-xs text-slate-500">
                          {notification.content_preview}
                        </p>
                        <p className="mt-2 text-[10px] text-slate-600">
                          {new Date(notification.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1">
                        {!notification.read && (
                          <button
                            className="text-slate-500 hover:text-primary"
                            onClick={() => handleMarkRead(notification.id)}
                            title={pickText(language, "Mark as read", "Segna come letto")}
                            type="button"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          className="text-slate-500 hover:text-red-400"
                          onClick={() => handleDelete(notification.id)}
                          title={pickText(language, "Delete", "Elimina")}
                          type="button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-white/10 p-3">
              <Link
                className="block text-center text-xs text-slate-400 hover:text-primary"
                onClick={() => setIsOpen(false)}
                to="/community"
              >
                {pickText(language, "View all discussions", "Vedi tutte le discussioni")}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
